import React, { useEffect, useState } from 'react';
import moment from 'moment';
import LadderLocalStorage from 'utilities/LadderLocalStorage';
import MaterialIcon from 'react/elements/MaterialIcon';
import MonthDayCell from './MonthDayCell';
import { useMealChipTooltip, MealTipContext } from './MealChipTooltip';
import { weeksFor, visibleRange } from './monthGridRange';

/**
 * The calendar's month view: a header of weekday names and one cell per day, in whole weeks.
 *
 * This replaced FullCalendar, which has no way to divide an all-day cell into meal slots. The
 * drag and drop is react-beautiful-dnd, under the dashboard's DragDropContext, like the grocery
 * lists it shares drags with.
 *
 * `actions` sits at the end of the header row, beside the month's name.
 *
 * A hovered meal shows its whole name and its meal idea's details (MealChipTooltip), from
 * `mealIdeasById`, the meal ideas state's map.
 *
 * `dayEvents` holds the meals and slot notes of the days shown, by 'YYYY-MM-DD', the days of the
 * months either side included (selectDayEvents), `eventsByDay` their calendar events
 * (selectCalendarEventsByDay), and `boughtListsByDay` the grocery lists bought on them
 * (selectBoughtListsByDay).
 *
 * On a phone the month is a compact seven-column grid unless the layout switch in the header is
 * set to List, which stacks the days one under another. Wider screens always get the grid and
 * never show the switch. The choice is this device's, remembered in local storage.
 */
export default function MonthGrid({
	selectedDate,
	dayEvents = null,
	eventsByDay = null,
	boughtListsByDay = null,
	dragging = false,
	mealIdeasById = null,
	onVisibleRange,
	onDayClick,
	actions = null,
}) {
	const monthKey = selectedDate.format('YYYY-MM');
	useEffect(() => {
		if(onVisibleRange) {
			const { start, end } = visibleRange(selectedDate);
			onVisibleRange(start, end);
		}
	}, [monthKey]);

	const { context: tipContext, tooltip } = useMealChipTooltip(dragging, mealIdeasById);
	const [layout, setLayout] = usePhoneLayout();

	const weeks = weeksFor(selectedDate);
	const today = moment();

	return (
		<div
			className={`month-grid${layout === LAYOUT_LIST ? ' month-grid--list' : ''}`}
			style={{ '--month-grid-weeks': weeks.length }}
		>
			<div className='month-grid__header'>
				<h5 className='month-grid__title'>{selectedDate.format('MMMM YYYY')}</h5>
				<div className='month-grid__layout' role='group' aria-label='Calendar layout'>
					{LAYOUTS.map(({ key, label, icon }) => (
						<button
							key={key}
							type='button'
							className={`btn-flat month-grid__layout-button${layout === key ? ' is-active' : ''}`}
							aria-pressed={layout === key}
							title={label}
							onClick={() => setLayout(key)}
						>
							<MaterialIcon>{icon}</MaterialIcon>
							<span>{label}</span>
						</button>
					))}
				</div>
				{actions &&
				<div className='month-grid__actions'>{actions}</div>
				}
			</div>
			<div className='month-grid__card'>
				<div className='month-grid__weekdays'>
					{weeks[0].map((day) => (
						<div key={day.day()} className='month-grid__weekday'>{day.format('ddd')}</div>
					))}
				</div>
				<MealTipContext.Provider value={tipContext}>
				<div className='month-grid__days'>
					{weeks.map((week) => week.map((day) => (
						<MonthDayCell
							key={day.format('YYYY-MM-DD')}
							date={day}
							dayEvent={dayEvents ? dayEvents[day.format('YYYY-MM-DD')] : null}
							calendarEvents={eventsByDay ? eventsByDay[day.format('YYYY-MM-DD')] : undefined}
							groceryLists={boughtListsByDay ? boughtListsByDay[day.format('YYYY-MM-DD')] : undefined}
							isOutside={!day.isSame(selectedDate, 'month')}
							isToday={day.isSame(today, 'day')}
							dragging={dragging}
							onDayClick={onDayClick}
						/>
					)))}
				</div>
				</MealTipContext.Provider>
			</div>
			{tooltip}
		</div>
	);
}

export const LAYOUT_GRID = 'grid';
export const LAYOUT_LIST = 'list';
export const LAYOUT_STORAGE_KEY = 'calendarPhoneLayout';

const LAYOUTS = [
	{ key: LAYOUT_GRID, label: 'Month', icon: 'view_module' },
	{ key: LAYOUT_LIST, label: 'List', icon: 'view_list' },
];

/** The phone layout this device last chose, the grid until it chooses. */
function usePhoneLayout() {
	const [layout, setLayout] = useState(() => {
		try {
			const stored = LadderLocalStorage.getItem(LAYOUT_STORAGE_KEY, LAYOUT_GRID);
			return stored === LAYOUT_LIST ? LAYOUT_LIST : LAYOUT_GRID;
		} catch(e) {
			return LAYOUT_GRID;
		}
	});
	const choose = (next) => {
		setLayout(next);
		try {
			LadderLocalStorage.setItem(LAYOUT_STORAGE_KEY, next);
		} catch(e) {
			// Private browsing or blocked storage: the choice lasts until the page reloads.
		}
	};
	return [layout, choose];
}
